import { G } from '@mobily/ts-belt';
import { Checkbox, Portal, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { any, arrayOf, bool, func, node, oneOf, shape, string } from 'prop-types';
import {
  ChangeEvent,
  Children,
  FC,
  isValidElement,
  MouseEventHandler,
  RefObject,
  useCallback,
  useMemo,
  useState,
} from 'react';
import {
  DatagridClasses,
  DatagridHeaderCell,
  DatagridHeaderProps,
  RaRecord,
  SortPayload,
  useListContext,
  useResourceContext,
  useTranslate,
} from 'react-admin';

const clsx = (...classes: (string | undefined)[]) => classes.filter(Boolean).join(' ');

type DatagridHeaderSortProps = {
  sortableColumns?: string[] | readonly string[];
  testId?: string;
  announcerContainerRef?: RefObject<HTMLDivElement>;
} & DatagridHeaderProps;

export const DatagridHeaderSort: FC<DatagridHeaderSortProps> = ({
  children,
  className,
  hasExpand = false,
  hasBulkActions = false,
  isRowSelectable,
  sortableColumns,
  testId,
  announcerContainerRef,
  ...restProps
}) => {
  const resource = useResourceContext(restProps);
  const translate = useTranslate();
  const { sort, data, onSelect, selectedIds, setSort } = useListContext<RaRecord>(restProps);
  const [announcement, setAnnouncement] = useState('');

  const updateSortCallback = useCallback<MouseEventHandler<HTMLSpanElement>>(
    (event) => {
      if (G.isNullable(setSort)) {
        return;
      }
      event.stopPropagation();

      const newField = event.currentTarget.dataset.field;
      if (sortableColumns && newField && !sortableColumns.includes(newField)) {
        return;
      }

      let newOrder = event.currentTarget.dataset.order as 'ASC' | 'DESC' | undefined;
      if (sort.field === newField) {
        newOrder = sort.order === 'ASC' ? 'DESC' : 'ASC';
      }

      if (newField && newOrder) {
        setSort({ field: newField, order: newOrder });

        const fieldLabel = translate(`resources.${resource}.fields.${newField}`, {
          _: newField,
        });

        const message = translate(
          newOrder === 'ASC'
            ? 'resources.bo_common.accessibility.sorted_ascending'
            : 'resources.bo_common.accessibility.sorted_descending',
          { field: fieldLabel },
        );

        setAnnouncement(message);
      }
    },
    [sort.field, sort.order, setSort, sortableColumns, translate, resource],
  );

  const handleSelectAll = useCallback(
    (event: ChangeEvent<HTMLInputElement>, _checked: boolean) => {
      onSelect(
        event.target.checked
          ? selectedIds.concat(
              data
                .filter((record) => !selectedIds.includes(record.id))
                .filter((record) => (isRowSelectable ? isRowSelectable(record) : true))
                .map((record) => record.id),
            )
          : [],
      );
    },
    [data, onSelect, isRowSelectable, selectedIds],
  );

  const selectableIds = useMemo(() => {
    if (!Array.isArray(data)) {
      return [];
    }
    return isRowSelectable
      ? data.filter((record) => isRowSelectable(record)).map((record) => record.id)
      : data.map((record) => record.id);
  }, [isRowSelectable, data]);

  return (
    <>
      <Portal container={() => announcerContainerRef?.current ?? null}>
        <Typography
          aria-live="polite"
          aria-relevant="additions text"
          aria-atomic={true}
          data-testid="TableFilteredColumnAnnouncerComponent"
          sx={{
            clip: 'rect(0 0 0 0)',
            clipPath: 'inset(50%)',
            height: '1px',
            overflow: 'hidden',
            position: 'fixed',
            top: 0,
            left: 0,
            whiteSpace: 'nowrap',
            width: '1px',
          }}>
          {announcement}
        </Typography>
      </Portal>
      <TableHead className={clsx(className, DatagridClasses.thead)}>
        <TableRow className={clsx(DatagridClasses.row, DatagridClasses.headerRow)}>
          {hasExpand && (
            <TableCell
              padding="none"
              className={clsx(DatagridClasses.headerCell, DatagridClasses.expandHeader)}
            />
          )}
          {hasBulkActions && G.isNotNullable(selectedIds) && (
            <TableCell padding="checkbox" className={DatagridClasses.headerCell}>
              <Checkbox
                aria-label={translate('ra.action.select_all', {
                  _: 'Select all',
                })}
                className="select-all"
                color="primary"
                data-testid={testId ? `${testId}_SelectAllCheckbox` : undefined}
                checked={
                  selectedIds.length > 0 &&
                  selectableIds.length > 0 &&
                  selectableIds.every((id) => selectedIds.includes(id))
                }
                onChange={handleSelectAll}
              />
            </TableCell>
          )}
          {Children.map(children, (field, index) =>
            isValidElement(field) ? (
              <DatagridHeaderCell
                className={clsx(
                  DatagridClasses.headerCell,
                  `column-${field.props.source as string}`,
                )}
                sort={sort}
                field={field}
                isSorting={sort.field === (field.props.sortBy || field.props.source)}
                key={field.props.source || index}
                resource={resource}
                updateSort={
                  sortableColumns && !sortableColumns.includes(field.props.source)
                    ? undefined
                    : updateSortCallback
                }
                sx={{
                  '& span:focus-visible': {
                    outline: '1px solid',
                    outlineOffset: '2px',
                  },
                }}
              />
            ) : null,
          )}
        </TableRow>
      </TableHead>
    </>
  );
};

DatagridHeaderSort.propTypes = {
  children: node,
  className: string,
  sort: shape({
    field: string.isRequired,
    order: oneOf<SortPayload['order']>(['ASC', 'DESC']).isRequired,
  }),
  data: arrayOf(any),
  hasExpand: bool,
  hasBulkActions: bool,
  isRowSelectable: func,
  isRowExpandable: func,
  onSelect: func,
  onToggleItem: func,
  resource: string,
  selectedIds: arrayOf(any),
  sortableColumns: arrayOf<string>(string.isRequired),
  setSort: func,
};

DatagridHeaderSort.displayName = 'DatagridHeaderSort';
